import React, {FC, useCallback, useEffect, useState} from 'react'
import {IBankAccountModel, initialBankAccount, bankAccountValidation} from './BankAccountModel'

import {createBankAccount, deleteBankAccount, getAllBankAccounts, updateBankAccount} from './Api'

import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {BankAccountTable} from './BankAccountTable'

import {currencies, branches} from '../../../../shared/models/SharedApiLookups'

export const BankAccountPage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [bankAccounts, setBankAccounts] = useState<IBankAccountModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('accountName')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize] = useState(10)
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  useEffect(() => {
    Promise.all([currencies(), branches()])
      .then(([cr, br]) => {
        setCurrencyOptions(cr ?? [])
        setBranchOptions(br ?? [])
      })
      .catch(() => {
        showErrorToast('Seçenekler yüklenemedi')
      })
  }, [])

  const fetchBankAccounts = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      try {
        setLoading(true)
        const res = await getAllBankAccounts(page, pageSize, sort, order)
        const data = res?.data?.payload || res?.data
        setBankAccounts(data?.content || data || [])
        setTotalPages(data?.totalPages || 1)
      } catch (error: any) {
        if (error?.response?.status === 401) {
          window.location.href = '/login'
        }
        console.error(error)
        showErrorToast('Yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  useEffect(() => {
    fetchBankAccounts(currentPage)
  }, [fetchBankAccounts, currentPage])

  const handleSave = async (values: IBankAccountModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateBankAccount({...values, id: selectedId})
        showSuccessToast('Banka hesabı başarıyla güncellendi')
      } else {
        await createBankAccount(values)
        showSuccessToast('Banka hesabı başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchBankAccounts(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      console.error(err)
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (row: IBankAccountModel, formik: FormikProps<IBankAccountModel>) => {
    formik.setValues({
      ...initialBankAccount,
      ...row,
    })
    setSelectedId(row.id ?? null)
    setTab('form')
  }

  const handleDeleteClick = useCallback((row: IBankAccountModel) => {
    setSelectedId(row.id ?? null)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId != null) {
      try {
        await deleteBankAccount(selectedId)
        showSuccessToast('Başarıyla silindi')
        await fetchBankAccounts(currentPage, sortBy, sortOrder)
      } catch (err) {
        console.error(err)
        showErrorToast('Silinirken bir hata oluştu')
      } finally {
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchBankAccounts(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchBankAccounts]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchBankAccounts(currentPage, column, order)
    },
    [currentPage, fetchBankAccounts]
  )

  const setSelectedIdFromHeader = (id: string | null) => {
    if (id == null || id === '') {
      setSelectedId(null)
      return
    }
    const n = Number(id)
    setSelectedId(Number.isNaN(n) ? null : n)
  }

  return (
    <>
      <Formik
        initialValues={initialBankAccount}
        validationSchema={bankAccountValidation}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='bankAccountForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedIdFromHeader}
                tabs={[
                  {key: 'form', title: 'Form'},
                  {key: 'list', title: 'Liste'},
                ]}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='bankAccountForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput
                        name='accountName'
                        label='Hesap Adı'
                        placeholder='Hesap No veya IBAN'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='bankName'
                        label='Banka Adı'
                        placeholder='Banka Adı'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6'>
                      <SelectInput
                        name='currency'
                        label='Hesap Dövizi'
                        options={currencyOptions}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='branchName'
                        label='Şube Adı'
                        placeholder='Şube Adı'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6'>
                      <SelectInput
                        name='operationBranch'
                        label='İşlem Şube'
                        options={branchOptions}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='specialCode'
                        label='Özel Kod'
                        placeholder='Özel Kod'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6 align-items-center'>
                      <div className='col-md-3'>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='active'
                            name='active'
                            checked={formik.values.active}
                            onChange={formik.handleChange}
                          />
                          <label className='form-check-label' htmlFor='active'>
                            Hesap Aktif mi?
                          </label>
                        </div>
                      </div>

                      <div className='col-md-3'>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='giftCheckAccount'
                            name='giftCheckAccount'
                            checked={formik.values.giftCheckAccount}
                            onChange={formik.handleChange}
                          />
                          <label className='form-check-label' htmlFor='giftCheckAccount'>
                            Hediye/Çek Hesabı mı?
                          </label>
                        </div>
                      </div>

                      <TextInput
                        name='email'
                        label='Eposta'
                        placeholder='Eposta'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h5 className='mb-0'>Banka Hesapları</h5>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-light-primary'
                          onClick={() => {
                            setTab('form')
                            formik.resetForm()
                            setSelectedId(null)
                          }}
                        >
                          Yeni
                        </button>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => fetchBankAccounts(0, sortBy, sortOrder)}
                        >
                          Yenile
                        </button>
                      </div>
                    </div>

                    <BankAccountTable
                      data={bankAccounts}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(row) => handleEdit(row, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu banka hesabını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default BankAccountPage
